/**
 * `/` — Home: the founder's "Today" (clavis-dashboard, `clavis-demo`).
 *
 * Mario, 2026-09-14: *"Redesign the homepage so it … includes all of the key
 * information needed as the Clavis founder and manager."* The page answers,
 * top to bottom, the questions whoever runs the studio asks first thing:
 *
 *   1. **Up next** — the sessions of the next 7 days, each with who it is
 *      (a link to the member), what and with whom, and the member's latest
 *      intake note inline (for a founding call, the caller's three answers).
 *      Each row also opens the booking on the calendar.
 *   2. **New people** — who joined in the last 7 days, with their stage tag,
 *      the channel they first talked on, and their first note; the 30-day
 *      count beside it.
 *   3. **Needs attention** — the cross-cutting sweep, unchanged.
 *   4. **Key metrics** — one compact row of tiles.
 *   5. **Calendar** — the week at a glance: the capacity figures, then one
 *      cell per studio day for the next 7 days, booked against free, each
 *      opening that day on the calendar. The session list is Up next's and
 *      is not repeated here (it was, as a table, until 2026-09-15).
 *   6. **Tasks** — when there are open ones; otherwise it stays shut.
 *
 * PR, Inbox, Flows, Analytics and the audit feed no longer have cards here:
 * they are operator-of-Layer surfaces and live behind "More" (the audit feed
 * is `/data/audit_log`). On `main` this page is still the ten-card stack.
 *
 * WHERE THE NUMBERS COME FROM. Everything but Tasks reads `/api/home-board`
 * (`home-board.ts`), which grew the two lists this page is built around — the
 * windowed next sessions with `member_id` / `slot_id` / `memory_latest`, and
 * `members.new_people` — and, for the Calendar card, `calendar.by_day`, the
 * seven studio days with their booked and free places; Tasks reads
 * `/api/tasks-board`, the endpoint that
 * backs `/tasks`, so its count can never disagree with that page. The two
 * requests fan out and each card renders as its own board answers.
 *
 * Cards keep the shared collapse chrome (`HomeCard`): every header is a real
 * `<h2>` wrapping the toggle, so the outline reads Home → Up next → New
 * people → … in order for a screen reader, and the headline stays visible
 * while a card is shut. `HOME_CARD_IDS` is the registry `Home.test.ts` pins
 * against the JSX; collapse state persists in `localStorage`.
 */

import { useEffect, useState, type ReactNode } from "react";
import { Link } from "react-router-dom";
import { api, type TasksBoardResponse } from "../lib/api";
import { buildCalendarDeepLink } from "../lib/calendar-deep-link";
import { isoToZonedDateKey, studioTimeZone } from "../lib/studio-tz";

// ─── shapes served by /api/home-board ──────────────────────────────────

interface HomeAttentionItem {
  readonly kind: string;
  readonly label: string;
  readonly detail: string | null;
  readonly at: string | null;
  readonly href: string | null;
}

/** One dated block of a member's rolling memory (`memory-entries.ts`). */
export interface HomeMemoryEntry {
  readonly date: string;
  readonly text: string;
}

export interface HomeNextSession {
  readonly booking_id: string;
  readonly slot_id: string;
  readonly starts_at: string;
  readonly ends_at: string;
  readonly member_id: string | null;
  readonly member_name: string | null;
  readonly trainer_name: string | null;
  readonly session_type: string | null;
  readonly location: string | null;
  readonly memory_latest: HomeMemoryEntry | null;
}

/** One cell of the Calendar card's week strip (`home-board.ts`). */
export interface HomeCalendarDay {
  /** `YYYY-MM-DD` in the studio's zone; the first is the studio's today. */
  readonly date: string;
  /** Live bookings that day — today's include the ones already held. */
  readonly booked: number;
  /** Open places on that day's slots that have not started yet. */
  readonly free: number;
}

export type HomeStage = "lead" | "trial" | "customer";

export interface HomeNewPerson {
  readonly member_id: string;
  readonly display_name: string | null;
  readonly created_at: string;
  readonly stage: HomeStage | null;
  readonly channel: string | null;
  readonly memory_first: HomeMemoryEntry | null;
}

interface HomeBoardResponse {
  readonly attention: ReadonlyArray<HomeAttentionItem>;
  readonly calendar: {
    readonly sessions_today: number;
    readonly sessions_next_7d: number;
    readonly open_capacity_next_7d: number;
    readonly slots_next_7d: number;
    readonly next_sessions: ReadonlyArray<HomeNextSession>;
    readonly by_day: ReadonlyArray<HomeCalendarDay>;
  };
  readonly members: {
    readonly joined_7d: number;
    readonly joined_prev_7d: number;
    readonly joined_30d: number;
    readonly total: number;
    readonly by_status: ReadonlyArray<{
      readonly status: string;
      readonly count: number;
    }>;
    readonly new_people: ReadonlyArray<HomeNewPerson>;
  };
  readonly metrics: {
    readonly credits_remaining_total: number;
    readonly credits_consumed_total: number;
    readonly packages_sold_total: number;
    readonly upcoming_sessions: number;
    readonly memberships_active: number;
    readonly upcoming_payments_30d: number;
    readonly revenue_30d_minor: number;
    readonly currency: string | null;
  };
  readonly computed_at: string;
}

// ─── card chrome ───────────────────────────────────────────────────────

const COLLAPSE_KEY = "home-collapsed-cards";

/**
 * Every card on the page, in render order — the founder's order, not the
 * old stack's. Asserted against the rendered ids by `Home.test.ts`, because
 * a card missing from here would silently open itself on a fresh browser.
 */
export const HOME_CARD_IDS = [
  "up_next",
  "new_people",
  "attention",
  "metrics",
  "calendar",
  "tasks",
] as const;

/**
 * The cards a first visit starts OPEN. The two lists ARE the page — a Today
 * view that hides today behind a click is not one — and the metrics row is
 * one line of tiles, cheap to show. Attention, Calendar and Tasks keep their
 * numbers in the headline and start shut, as before.
 */
export const DEFAULT_EXPANDED_CARD_IDS: ReadonlySet<string> = new Set([
  "up_next",
  "new_people",
  "metrics",
]);

function defaultCollapsed(): ReadonlySet<string> {
  return new Set(
    HOME_CARD_IDS.filter((id) => !DEFAULT_EXPANDED_CARD_IDS.has(id)),
  );
}

function loadCollapsed(): ReadonlySet<string> {
  try {
    const raw = window.localStorage.getItem(COLLAPSE_KEY);
    if (raw === null) return defaultCollapsed();
    const parsed: unknown = JSON.parse(raw);
    return Array.isArray(parsed) ? new Set(parsed.map(String)) : defaultCollapsed();
  } catch {
    return defaultCollapsed();
  }
}

export interface HomeCardProps {
  readonly id: string;
  readonly title: string;
  /** Stays visible when the card is shut — the reason not to open it. */
  readonly headline: ReactNode;
  readonly to?: string;
  /** The footer link's noun when it is not the card's title ("Open calendar →" under Up next). */
  readonly toLabel?: string;
  readonly collapsed: boolean;
  readonly onToggle: (id: string) => void;
  /**
   * A card with nothing to show stays shut and its toggle is inert (Tasks
   * with no open task). The headline still says why.
   */
  readonly locked?: boolean;
  readonly error?: string | null;
  readonly loading?: boolean;
  readonly children: ReactNode;
}

/**
 * The shared card: a `<section>` named by its `<h2>`, whose whole header is
 * the disclosure button (a bar you cannot miss beats a caret you have to
 * hit). A locked card renders no body and a disabled button.
 */
export function HomeCard(props: HomeCardProps): JSX.Element {
  const { id, title, headline, to, toLabel, onToggle } = props;
  const locked = props.locked === true;
  const collapsed = locked || props.collapsed;
  const titleId = `home-card-${id}-title`;
  const bodyId = `home-card-${id}-body`;
  return (
    <section className="home-card" aria-labelledby={titleId}>
      <h2 className="home-card-h">
        <button
          type="button"
          className="home-card-head"
          aria-expanded={!collapsed}
          aria-controls={bodyId}
          disabled={locked}
          onClick={() => onToggle(id)}
        >
          <span className="home-card-caret" aria-hidden="true">
            {collapsed ? "▸" : "▾"}
          </span>
          <span className="home-card-title" id={titleId}>
            {title}
          </span>
          <span className="home-card-headline">
            {props.loading === true
              ? "loading…"
              : props.error != null
                ? `unavailable — ${props.error}`
                : headline}
          </span>
        </button>
      </h2>
      {collapsed ? null : (
        <div className="home-card-body" id={bodyId}>
          {props.error != null ? (
            <p className="empty">Couldn&apos;t load this section ({props.error}).</p>
          ) : props.loading === true ? (
            <p className="empty">Loading…</p>
          ) : (
            props.children
          )}
          {to !== undefined ? (
            <p className="home-card-more">
              <Link to={to}>Open {(toLabel ?? title).toLowerCase()} →</Link>
            </p>
          ) : null}
        </div>
      )}
    </section>
  );
}

/** A label/value pair rendered in the shared `.stat-grid` gauge block. */
function Stats(props: {
  readonly items: ReadonlyArray<{
    readonly label: string;
    readonly value: string;
  }>;
}): JSX.Element {
  return (
    <div className="stat-grid">
      {props.items.map((s) => (
        <div className="stat-card" key={s.label}>
          <div className="label">{s.label}</div>
          <div className="value">{s.value}</div>
        </div>
      ))}
    </div>
  );
}

// ─── formatting ────────────────────────────────────────────────────────

const int = (n: number): string => n.toLocaleString("en-GB");

/**
 * `count(1, "credit")` → "1 credit"; `count(4, "credit")` → "4 credits".
 * "1 credits outstanding" reads as a bug in the number.
 */
export const count = (n: number, noun: string, plural = `${noun}s`): string =>
  `${int(n)} ${n === 1 ? noun : plural}`;

function money(minor: number, currency: string | null): string {
  const ccy = (currency ?? "GBP").toUpperCase();
  try {
    return new Intl.NumberFormat("en-GB", {
      style: "currency",
      currency: ccy,
    }).format(minor / 100);
  } catch {
    return `${(minor / 100).toFixed(2)} ${ccy}`;
  }
}

function when(iso: string | null): string {
  if (iso === null) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleString("en-GB", {
    timeZone: studioTimeZone(),
    day: "2-digit",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  });
}

/** "+3" / "−2" / "level" against the previous window. */
function delta(now: number, prev: number): string {
  const d = now - prev;
  if (d === 0) return "level with the 7 days before";
  return `${d > 0 ? "+" : "−"}${Math.abs(d)} vs the 7 days before`;
}

/** "Today" / "Tomorrow" for a studio-day key, else null; `now` is injectable. */
function relativeDayName(key: string, now: Date): "Today" | "Tomorrow" | null {
  const todayKey = isoToZonedDateKey(now.toISOString());
  const tomorrowKey = isoToZonedDateKey(
    new Date(now.getTime() + 24 * 60 * 60 * 1000).toISOString(),
  );
  if (key === todayKey) return "Today";
  if (key === tomorrowKey) return "Tomorrow";
  return null;
}

/**
 * The day a row belongs to, in the studio's calendar: "Today", "Tomorrow",
 * else "Tue 15 Sep". Compares ZONED date keys (`isoToZonedDateKey`), never
 * browser-local ones — a 23:30Z session is the next London day. `now` is
 * injectable for tests.
 */
export function dayLabel(iso: string, now: Date = new Date()): string {
  const key = isoToZonedDateKey(iso);
  if (key === "") return "—";
  return (
    relativeDayName(key, now) ??
    new Date(iso).toLocaleDateString("en-GB", {
      timeZone: studioTimeZone(),
      weekday: "short",
      day: "numeric",
      month: "short",
    })
  );
}

/**
 * A `YYYY-MM-DD` key as a UTC-midnight `Date`, or null when it is not a real
 * calendar day. Deliberately NOT `new Date(key)` through the browser's zone
 * (the previous local day for any viewer behind UTC) and NOT a bare
 * `Date.UTC` (which rolls `2026-13-40` forward into a real date).
 */
function dateFromKey(key: string): Date | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(key);
  if (m === null) return null;
  const [y, mo, d] = [Number(m[1]), Number(m[2]), Number(m[3])];
  const out = new Date(Date.UTC(y, mo - 1, d));
  return out.getUTCFullYear() === y &&
    out.getUTCMonth() === mo - 1 &&
    out.getUTCDate() === d
    ? out
    : null;
}

/**
 * A studio-day KEY (as `calendar.by_day` carries it) as the two lines of a
 * week-strip cell: the name ("Today" / "Tomorrow" / "Thu") and the date
 * ("17 Sept"). The key already IS the studio's day, so it is formatted as a
 * UTC date — never through the browser's zone. An unparseable key answers
 * an em dash and no date.
 */
export function dayKeyParts(
  key: string,
  now: Date = new Date(),
): { readonly name: string; readonly date: string } {
  const d = dateFromKey(key);
  if (d === null) return { name: "—", date: "" };
  const fmt = (options: Intl.DateTimeFormatOptions): string =>
    d.toLocaleDateString("en-GB", { timeZone: "UTC", ...options });
  return {
    name: relativeDayName(key, now) ?? fmt({ weekday: "short" }),
    date: fmt({ day: "numeric", month: "short" }),
  };
}

/** "10:00–10:20" in the studio zone; a lone start when the end is unparseable. */
export function timeRange(startIso: string, endIso: string): string {
  const fmt = (iso: string): string | null => {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return null;
    return d.toLocaleTimeString("en-GB", {
      timeZone: studioTimeZone(),
      hour: "2-digit",
      minute: "2-digit",
    });
  };
  const a = fmt(startIso);
  const b = fmt(endIso);
  if (a === null) return "—";
  return b === null ? a : `${a}–${b}`;
}

/** `founding-call` → "Founding call"; null → "Session". */
export function prettySessionType(slug: string | null): string {
  if (slug === null || slug.trim() === "") return "Session";
  const words = slug.replace(/[-_]+/g, " ").trim();
  return words.charAt(0).toUpperCase() + words.slice(1);
}

const CHANNEL_LABELS: Readonly<Record<string, string>> = {
  whatsapp: "WhatsApp",
  instagram: "Instagram",
  email: "Email",
  web_chat: "Web chat",
  sms: "SMS",
};

/** The channel a person first talked on, as a word; null → "no channel yet". */
export function channelLabel(channel: string | null): string {
  if (channel === null) return "no channel yet";
  return CHANNEL_LABELS[channel] ?? channel.replace(/_/g, " ");
}

const STAGE_LABELS: Readonly<Record<HomeStage, string>> = {
  lead: "Lead",
  trial: "Trial",
  customer: "Customer",
};

/** The stage tag as a word; null → "no stage yet" (the reconciler has not run). */
export function stageLabel(stage: HomeStage | null): string {
  return stage === null ? "no stage yet" : STAGE_LABELS[stage];
}

/**
 * Whether a note is long enough to be worth a "More" toggle. Two lines at the
 * card's measure are ~160 characters; a line break always costs a line. The
 * clamp itself is CSS (`.home-note`), this only decides whether to offer the
 * button.
 */
export const NOTE_TOGGLE_CHARS = 160;
export function noteNeedsToggle(text: string): boolean {
  return text.length > NOTE_TOGGLE_CHARS || text.includes("\n");
}

// ─── rows ──────────────────────────────────────────────────────────────

interface NoteLineProps {
  readonly entry: HomeMemoryEntry;
  readonly open: boolean;
  readonly onToggle: () => void;
}

/** One memory entry, clamped to two lines until opened. */
function NoteLine(props: NoteLineProps): JSX.Element {
  const { entry, open, onToggle } = props;
  return (
    <>
      <p className={`home-note${open ? " is-open" : ""}`} title={entry.date}>
        {entry.text}
      </p>
      {noteNeedsToggle(entry.text) ? (
        <button
          type="button"
          className="home-note-toggle"
          aria-expanded={open}
          onClick={onToggle}
        >
          {open ? "Less" : "More"}
        </button>
      ) : null}
    </>
  );
}

export interface UpNextRowsProps {
  readonly sessions: ReadonlyArray<HomeNextSession>;
  /** Rows whose note is opened past two lines, by booking id. */
  readonly openNotes: ReadonlySet<string>;
  readonly onToggleNote: (bookingId: string) => void;
  readonly now?: Date;
}

/**
 * The next sessions as a list of rows — a person each, not a table: the
 * member's name is the link, the meta line says what and with whom, the note
 * sits under it, and the calendar deep link opens the booking itself.
 */
export function UpNextRows(props: UpNextRowsProps): JSX.Element {
  return (
    <ul className="home-rows">
      {props.sessions.map((s) => {
        const day = dayLabel(s.starts_at, props.now);
        return (
          <li className="home-row" key={s.booking_id}>
            <div className="home-row-when">
              <span className={`home-row-day${day === "Today" ? " is-today" : ""}`}>
                {day}
              </span>
              <span className="home-row-time">{timeRange(s.starts_at, s.ends_at)}</span>
            </div>
            <div className="home-row-main">
              <div className="home-row-title">
                {s.member_id !== null ? (
                  <Link to={`/members/${s.member_id}`}>
                    {s.member_name ?? "Unnamed member"}
                  </Link>
                ) : (
                  <span>{s.member_name ?? "—"}</span>
                )}
              </div>
              <div className="home-row-meta">
                {prettySessionType(s.session_type)}
                {" · "}
                {s.trainer_name ?? "no trainer"}
                {s.location !== null ? ` · ${s.location}` : ""}
              </div>
              {s.memory_latest !== null ? (
                <NoteLine
                  entry={s.memory_latest}
                  open={props.openNotes.has(s.booking_id)}
                  onToggle={() => props.onToggleNote(s.booking_id)}
                />
              ) : null}
            </div>
            <div className="home-row-actions">
              <Link to={buildCalendarDeepLink(s.slot_id, s.starts_at)}>
                Open in calendar →
              </Link>
            </div>
          </li>
        );
      })}
    </ul>
  );
}

export interface NewPeopleRowsProps {
  readonly people: ReadonlyArray<HomeNewPerson>;
  readonly openNotes: ReadonlySet<string>;
  readonly onToggleNote: (memberId: string) => void;
  readonly now?: Date;
}

/** The week's joiners: name (a link), stage and channel chips, first note. */
export function NewPeopleRows(props: NewPeopleRowsProps): JSX.Element {
  return (
    <ul className="home-rows">
      {props.people.map((p) => {
        const day = dayLabel(p.created_at, props.now);
        return (
          <li className="home-row" key={p.member_id}>
            <div className="home-row-when">
              <span className={`home-row-day${day === "Today" ? " is-today" : ""}`}>
                {day}
              </span>
              <span className="home-row-time">{timeRange(p.created_at, "")}</span>
            </div>
            <div className="home-row-main">
              <div className="home-row-title">
                <Link to={`/members/${p.member_id}`}>
                  {p.display_name ?? "Unnamed member"}
                </Link>
                <span className={`tag home-stage home-stage-${p.stage ?? "none"}`}>
                  {stageLabel(p.stage)}
                </span>
                <span className="tag home-chip">{channelLabel(p.channel)}</span>
              </div>
              {p.memory_first !== null ? (
                <NoteLine
                  entry={p.memory_first}
                  open={props.openNotes.has(p.member_id)}
                  onToggle={() => props.onToggleNote(p.member_id)}
                />
              ) : (
                <p className="home-note subtle">No notes on file yet.</p>
              )}
            </div>
            <div className="home-row-actions">
              <Link to={`/members/${p.member_id}`}>Open profile →</Link>
            </div>
          </li>
        );
      })}
    </ul>
  );
}

export interface CalendarDaysProps {
  readonly days: ReadonlyArray<HomeCalendarDay>;
  readonly now?: Date;
}

/**
 * The Calendar card's week: one cell per studio day, today first, each a
 * link to that day on the calendar (`/calendar?date=`, which the page
 * anchors its view on). Booked against free, as a bar and in words; a day
 * with neither — a closed day, or today once its last slot has started —
 * says so rather than "0 booked · 0 free". The session LIST is Up next's;
 * this is the shape of the week.
 */
export function CalendarDays(props: CalendarDaysProps): JSX.Element {
  return (
    <ol className="home-days">
      {props.days.map((d) => {
        const { name, date } = dayKeyParts(d.date, props.now);
        const places = d.booked + d.free;
        const bookedPct = places === 0 ? 0 : Math.round((d.booked / places) * 100);
        return (
          <li
            className={`home-day${name === "Today" ? " is-today" : ""}${
              places === 0 ? " is-quiet" : ""
            }`}
            key={d.date}
          >
            <Link
              className="home-day-link"
              to={`/calendar?date=${encodeURIComponent(d.date)}`}
            >
              <span className="home-day-name">{name}</span>
              <span className="home-day-date">{date}</span>
              <span className="home-day-bar" aria-hidden="true">
                <span className="home-day-fill" style={{ width: `${bookedPct}%` }} />
              </span>
              <span className="home-day-figures">
                {places === 0
                  ? "no open slots"
                  : `${int(d.booked)} booked · ${int(d.free)} free`}
              </span>
            </Link>
          </li>
        );
      })}
    </ol>
  );
}

// ─── page ──────────────────────────────────────────────────────────────

interface Slot<T> {
  readonly data: T | null;
  readonly error: string | null;
}

const pending = <T,>(): Slot<T> => ({ data: null, error: null });

export function Home(): JSX.Element {
  const [collapsed, setCollapsed] = useState<ReadonlySet<string>>(loadCollapsed);
  const [home, setHome] = useState<Slot<HomeBoardResponse>>(pending);
  const [tasks, setTasks] = useState<Slot<TasksBoardResponse>>(pending);
  // Notes opened past their two-line clamp, keyed by row (booking id on Up
  // next, member id on New people). Page state rather than row state so a
  // card collapse does not silently re-clamp what the reader opened.
  const [openNotes, setOpenNotes] = useState<ReadonlySet<string>>(new Set());

  useEffect(() => {
    let cancelled = false;
    // Each request settles into its own slot, so a card renders as soon as
    // ITS board answers rather than waiting on the slower of the two.
    const land =
      <T,>(set: (s: Slot<T>) => void) =>
      (p: Promise<T>): void => {
        p.then(
          (data) => {
            if (!cancelled) set({ data, error: null });
          },
          (e: Error) => {
            if (!cancelled) set({ data: null, error: e.message });
          },
        );
      };
    land(setHome)(
      api.get<HomeBoardResponse>(
        `/api/home-board?tz=${encodeURIComponent(studioTimeZone())}`,
      ),
    );
    land(setTasks)(api.tasksBoard());
    return () => {
      cancelled = true;
    };
  }, []);

  const toggle = (id: string): void => {
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      try {
        window.localStorage.setItem(COLLAPSE_KEY, JSON.stringify([...next]));
      } catch {
        /* private mode — the card state just won't survive a reload */
      }
      return next;
    });
  };

  const toggleNote = (key: string): void => {
    setOpenNotes((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const card = (id: string): { collapsed: boolean; onToggle: (i: string) => void } => ({
    collapsed: collapsed.has(id),
    onToggle: toggle,
  });

  const h = home.data;
  const homeLoading = home.data === null && home.error === null;
  const openTasks =
    tasks.data === null
      ? 0
      : tasks.data.managers.reduce((n, a) => n + a.tasks.length, 0) +
        tasks.data.trainers.reduce((n, a) => n + a.tasks.length, 0) +
        tasks.data.unassigned.length;

  return (
    <div className="page-home">
      <h1 className="page-title">Home</h1>

      {/* ── up next ─────────────────────────────────────────────────── */}
      <HomeCard
        id="up_next"
        title="Up next"
        to="/calendar"
        toLabel="calendar"
        loading={homeLoading}
        error={home.error}
        headline={
          h == null
            ? ""
            : `${count(h.calendar.sessions_next_7d, "session")} in the next 7 days · ${int(
                h.calendar.sessions_today,
              )} today`
        }
        {...card("up_next")}
      >
        {h == null ? null : h.calendar.next_sessions.length === 0 ? (
          <p className="empty">
            Nothing booked in the next 7 days. New bookings appear here the
            moment they land.
          </p>
        ) : (
          <UpNextRows
            sessions={h.calendar.next_sessions}
            openNotes={openNotes}
            onToggleNote={toggleNote}
          />
        )}
      </HomeCard>

      {/* ── new people ──────────────────────────────────────────────── */}
      <HomeCard
        id="new_people"
        title="New people"
        to="/members"
        toLabel="members"
        loading={homeLoading}
        error={home.error}
        headline={
          h == null
            ? ""
            : `${count(h.members.joined_7d, "person", "people")} in 7 days · ${int(
                h.members.joined_30d,
              )} in 30 · ${int(h.members.total)} total`
        }
        {...card("new_people")}
      >
        {h == null ? null : h.members.new_people.length === 0 ? (
          <p className="empty">
            Nobody new in the last 7 days — {int(h.members.joined_30d)} joined in
            the last 30, {delta(h.members.joined_7d, h.members.joined_prev_7d)}.
          </p>
        ) : (
          <>
            <NewPeopleRows
              people={h.members.new_people}
              openNotes={openNotes}
              onToggleNote={toggleNote}
            />
            <p className="member-note">
              {int(h.members.joined_30d)} joined in the last 30 days ·{" "}
              {delta(h.members.joined_7d, h.members.joined_prev_7d)}.
            </p>
          </>
        )}
      </HomeCard>

      {/* ── needs attention ─────────────────────────────────────────── */}
      <HomeCard
        id="attention"
        title="Needs attention"
        loading={homeLoading}
        error={home.error}
        headline={
          h === undefined || h === null
            ? ""
            : h.attention.length === 0
              ? "nothing flagged"
              : count(h.attention.length, "item")
        }
        {...card("attention")}
      >
        {h !== null && h !== undefined && h.attention.length === 0 ? (
          <p className="empty">
            Nothing flagged — no failed checkouts, expiring credits, overdue
            tasks or overbooked sessions.
          </p>
        ) : (
          <table>
            <thead>
              <tr>
                <th>What</th>
                <th>Detail</th>
                <th>When</th>
              </tr>
            </thead>
            <tbody>
              {(h?.attention ?? []).map((a, i) => (
                <tr key={`${a.kind}-${i}`}>
                  <td>
                    {a.href !== null ? (
                      <Link to={a.href}>{a.label}</Link>
                    ) : (
                      a.label
                    )}
                  </td>
                  <td>{a.detail ?? "—"}</td>
                  <td>{when(a.at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </HomeCard>

      {/* ── key metrics ─────────────────────────────────────────────── */}
      <HomeCard
        id="metrics"
        title="Key metrics"
        loading={homeLoading}
        error={home.error}
        headline={
          h == null
            ? ""
            : `${count(h.metrics.credits_remaining_total, "credit")} outstanding · ${count(
                h.metrics.upcoming_sessions,
                "session",
              )} booked ahead · ${money(h.metrics.revenue_30d_minor, h.metrics.currency)} in 30 days`
        }
        {...card("metrics")}
      >
        {h == null ? null : (
          <div className="home-metrics-row">
            <Stats
              items={[
                {
                  label: "Credits outstanding",
                  value: int(h.metrics.credits_remaining_total),
                },
                {
                  label: "Credits used",
                  value: int(h.metrics.credits_consumed_total),
                },
                {
                  label: "Packages sold",
                  value: int(h.metrics.packages_sold_total),
                },
                {
                  label: "Sessions booked ahead",
                  value: int(h.metrics.upcoming_sessions),
                },
                {
                  label: "Active memberships",
                  value: int(h.metrics.memberships_active),
                },
                {
                  label: "Renewals · next 30d",
                  value: int(h.metrics.upcoming_payments_30d),
                },
                {
                  label: "Revenue · last 30d",
                  value: money(h.metrics.revenue_30d_minor, h.metrics.currency),
                },
              ]}
            />
          </div>
        )}
      </HomeCard>

      {/* ── calendar ────────────────────────────────────────────────── */}
      <HomeCard
        id="calendar"
        title="Calendar"
        to="/calendar"
        loading={homeLoading}
        error={home.error}
        headline={
          h == null
            ? ""
            : `${int(h.calendar.sessions_today)} today · ${int(
                h.calendar.sessions_next_7d,
              )} in the next 7 days · ${int(h.calendar.open_capacity_next_7d)} open places`
        }
        {...card("calendar")}
      >
        {h == null ? null : (
          <>
            <Stats
              items={[
                { label: "Sessions today", value: int(h.calendar.sessions_today) },
                {
                  label: "Sessions · next 7d",
                  value: int(h.calendar.sessions_next_7d),
                },
                { label: "Slots · next 7d", value: int(h.calendar.slots_next_7d) },
                {
                  label: "Open capacity · next 7d",
                  value: int(h.calendar.open_capacity_next_7d),
                },
              ]}
            />
            <CalendarDays days={h.calendar.by_day} />
          </>
        )}
      </HomeCard>

      {/* ── tasks — stays shut when there is nothing open ───────────── */}
      <HomeCard
        id="tasks"
        title="Tasks"
        to="/tasks"
        loading={tasks.data === null && tasks.error === null}
        error={tasks.error}
        locked={tasks.data !== null && openTasks === 0}
        headline={
          tasks.data === null
            ? ""
            : openTasks === 0
              ? "no open tasks"
              : `${int(openTasks)} open · ${int(tasks.data.unassigned.length)} unassigned`
        }
        {...card("tasks")}
      >
        {tasks.data === null ? null : (
          <table>
            <thead>
              <tr>
                <th>Assignee</th>
                <th>Open tasks</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>General · unassigned</td>
                <td>{int(tasks.data.unassigned.length)}</td>
              </tr>
              {[...tasks.data.managers, ...tasks.data.trainers].map((a) => (
                <tr key={`${a.kind}-${a.id}`}>
                  <td>{a.display_name ?? a.id}</td>
                  <td>{int(a.tasks.length)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </HomeCard>
    </div>
  );
}
